// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Result of parsing a serial file (CSV or TXT).
 */
interface ParseSerialsResult {
  /** Successfully parsed serials (deduplicated within the file) */
  serials: string[];
  /** Count of duplicate serials found within the file itself */
  duplicatesInFile: number;
}

/**
 * Parses a file containing serials and returns deduplicated results.
 *
 * Supported formats:
 * - Plain text (TXT): One serial per line
 * - CSV with header: Looks for "serial" or "serials" column, ignores first row
 * - CSV without recognized header: Treats as plain text (one entry per line)
 *
 * @param text - The file content as a string
 * @param fileName - The original file name (used to determine format)
 * @returns ParseSerialsResult with deduplicated serials and duplicate count
 */
export function parseSerialsFile(
  text: string,
  fileName: string
): ParseSerialsResult {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');

  if (lines.length === 0) {
    return { serials: [], duplicatesInFile: 0 };
  }

  const isCSV = fileName.toLowerCase().endsWith('.csv');
  let rawSerials: string[] = [];

  if (isCSV && lines.length > 1) {
    // Try to parse as CSV with headers
    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const serialIndex = headers.findIndex(
      (h) => h === 'serial' || h === 'serials'
    );

    if (serialIndex !== -1) {
      // Found serial column - extract values from that column
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',');
        const serial = values[serialIndex]?.trim();
        if (serial) {
          rawSerials.push(serial);
        }
      }
    } else {
      // No serial column found - treat each line as a serial
      rawSerials = lines.map((line) => line.trim()).filter(Boolean);
    }
  } else {
    // Plain text - each line is a serial
    rawSerials = lines.map((line) => line.trim()).filter(Boolean);
  }

  // Deduplicate within file using case-insensitive comparison
  const seen = new Set<string>();
  const uniqueSerials: string[] = [];
  let duplicatesInFile = 0;

  for (const serial of rawSerials) {
    const lowerSerial = serial.toLowerCase();
    if (seen.has(lowerSerial)) {
      duplicatesInFile++;
    } else {
      seen.add(lowerSerial);
      uniqueSerials.push(serial);
    }
  }

  return { serials: uniqueSerials, duplicatesInFile };
}
